import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, MessageCircle, Clock, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const LOGO_URL = "/logo.png";

const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="relative bg-zinc-900 text-white">
      {/* checkered stripe top */}
      <div className="checkered-row" aria-hidden="true">
        {Array.from({ length: 60 }).map((_, i) => (<span key={i} />))}
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
        {/* Brand */}
        <div className="lg:col-span-5">
          <Link to="/" className="flex items-center gap-3 mb-5">
            <img src={LOGO_URL} alt="Brothers Taxi" className="h-14 w-14 object-contain" />
            <div className="leading-none">
              <div className="font-display font-black text-white text-lg uppercase">Brothers</div>
              <div className="font-display font-black text-[#FFD500] text-lg uppercase -mt-0.5">Taxi</div>
            </div>
          </Link>
          <p className="text-zinc-400 text-sm leading-relaxed max-w-md">{t.footer.tagline}</p>

          <div className="mt-7 flex gap-3">
            <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="h-11 w-11 border-2 border-zinc-700 rounded-full flex items-center justify-center hover:bg-[#FFD500] hover:text-zinc-900 hover:border-[#FFD500] transition-colors">
              <Instagram className="h-4 w-4" />
            </a>
            <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="h-11 w-11 border-2 border-zinc-700 rounded-full flex items-center justify-center hover:bg-[#FFD500] hover:text-zinc-900 hover:border-[#FFD500] transition-colors">
              <Facebook className="h-4 w-4" />
            </a>
            <a href="https://wa.me/355682222286" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" aria-label="WhatsApp" className="h-11 w-11 border-2 border-zinc-700 rounded-full flex items-center justify-center hover:bg-[#FFD500] hover:text-zinc-900 hover:border-[#FFD500] transition-colors">
              <MessageCircle className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#FFD500] mb-5">{t.footer.quickLinks}</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/" className="text-zinc-300 hover:text-[#FFD500] transition-colors inline-flex items-center gap-1 group">{t.nav.home}<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" /></Link></li>
            <li><Link to="/about" className="text-zinc-300 hover:text-[#FFD500] transition-colors">{t.nav.about}</Link></li>
            <li><Link to="/fleet" className="text-zinc-300 hover:text-[#FFD500] transition-colors">{t.nav.fleet}</Link></li>
            <li><Link to="/pricing" className="text-zinc-300 hover:text-[#FFD500] transition-colors">{t.nav.pricing}</Link></li>
            <li><Link to="/airport-transfers" className="text-zinc-300 hover:text-[#FFD500] transition-colors">{t.nav.airport}</Link></li>
            <li><Link to="/contact" className="text-zinc-300 hover:text-[#FFD500] transition-colors">{t.nav.contact}</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-4">
          <h4 className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#FFD500] mb-5">{t.footer.contact}</h4>
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3 text-zinc-300">
              <span className="h-9 w-9 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                <Phone className="h-4 w-4 text-[#FFD500]" />
              </span>
              <a href="tel:+355682222286" className="hover:text-[#FFD500] transition-colors font-bold text-base">+355 68 22 22 286</a>
            </li>
            <li className="flex items-start gap-3 text-zinc-300">
              <span className="h-9 w-9 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                <Mail className="h-4 w-4 text-[#FFD500]" />
              </span>
              <a href="mailto:booking@brotherstaxi.al" className="hover:text-[#FFD500] transition-colors">booking@brotherstaxi.al</a>
            </li>
            <li className="flex items-start gap-3 text-zinc-300">
              <span className="h-9 w-9 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                <MapPin className="h-4 w-4 text-[#FFD500]" />
              </span>
              <span>{t.contact.locationValue}</span>
            </li>
            <li className="flex items-start gap-3 text-zinc-300">
              <span className="h-9 w-9 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                <Clock className="h-4 w-4 text-[#FFD500]" />
              </span>
              <span>24/7 — {t.common.available247}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <p data-testid="footer-copy">© {year} Brothers Taxi. {t.footer.rights}</p>
          <p>
            <a href="https://brotherstaxi.al" className="hover:text-[#FFD500]">brotherstaxi.al</a>
            <span className="mx-2">·</span>
            <a href="https://app.brotherstaxi.al" className="hover:text-[#FFD500]">app.brotherstaxi.al</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
